/** @jsxImportSource @emotion/react */
import * as yup from "yup";
import { css } from "@emotion/react";
import { FormProvider, useController, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import { useNavigate, useParams } from "react-router";
import { yupResolver } from "@hookform/resolvers/yup";
import * as authSelectors from "../../auth/authSelectors";
import { useGetCampaigns } from "../../auto-campaigns/campaigns/service/campaign-api";
import {
  cacheKeys,
  useAddCampaignToMenu,
  useGetMenuCampaigns,
} from "../service/menus-api";
import { getErrorMessage } from "../../../error-handler";
import { SpinnerBlock } from "../../common/Spinner";
import { StyledText } from "../../../assets/typography/typography";
import { FormRow } from "../../common/hook-form";
import { SelectField } from "../../../components/forms/fields/SelectField";
import {
  ButtonAction,
  ButtonInternalLink,
} from "../../../components/NewButtons";
import { ActionSpinner } from "../../../components/ActionSpinner";
import { colors } from "../../../components/colors";
import { useQueryClient } from "react-query";

export const buttonWrapperStyle = css`
  position: sticky;
  bottom: 0;
  z-index: 100;
  background-color: ${colors.white};
  display: flex;
  flex-direction: row;
  padding: 16px 24px;
  border-top: 1px solid ${colors.greyBorder};
  gap: 8px;
  width: 100%;
  align-items: center;
  height: fit-content;
  margin-top: auto;
  border-radius: 0px 0px 8px 8px;
  justify-content: end;
`;

export const formStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  overflow-y: auto;
`;

export const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding-top: 24px;
  padding-bottom: 24px;
  width: 600px;
`;

export const bodySectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

export const bodyHeaderStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const schema = yup.object({
  ids: yup.array(yup.string()).min(1, "At least one campaign is required"),
});

type FormValues = yup.InferType<typeof schema>;

export const AddMenuCampaignsForm = () => {
  const { id: menuId } = useParams<{ id: string }>();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const methods = useForm<FormValues>({
    defaultValues: {
      ids: [],
    },
    resolver: yupResolver(schema),
  });

  const {
    field: { value, onChange, name, ref, ...rest },
  } = useController({
    control: methods.control,
    name: "ids",
  });

  const { data: campaigns, isLoading: isLoadingCampaigns } = useGetCampaigns(
    organizationKeyName
  );

  const {
    data: menuCampaigns,
    isLoading: isLoadingMenuCampaigns,
  } = useGetMenuCampaigns(organizationKeyName, menuId);

  const { mutateAsync: addCampaignToMenu } = useAddCampaignToMenu();

  const onSubmit = async (data: FormValues) => {
    if (!menuId) {
      toastr.error("Error", "Menu ID is required");
      return;
    }
    try {
      for (const id of data?.ids || []) {
        if (!id) {
          continue;
        }
        await addCampaignToMenu({
          organizationKeyName,
          data: { id, menuId },
        });
      }
      queryClient.refetchQueries([cacheKeys.menuCampaigns, menuId]);
      toastr.success(
        "Success",
        `Successfully added ${data?.ids?.length} campaign${
          data?.ids?.length === 1 ? "" : "s"
        } to menu`
      );
      navigate("..");
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  if (isLoadingCampaigns || isLoadingMenuCampaigns) {
    return <SpinnerBlock />;
  }

  return (
    <FormProvider {...methods}>
      <form css={formStyle} onSubmit={methods.handleSubmit(onSubmit)}>
        <div css={bodyStyle}>
          <div css={bodySectionStyle}>
            <div css={bodyHeaderStyle}>
              <StyledText className="bodyTextSemibold mb-0">
                Campaigns
              </StyledText>
              <StyledText className="bodySubtextRegular mb-0">
                Choose campaigns to add to this menu
              </StyledText>
            </div>
            <FormRow>
              <SelectField
                isMulti
                closeMenuOnSelect={false}
                placeholder="Start typing to search for campaigns..."
                errorMessage={
                  methods.getFieldState("ids", methods.formState).error?.message
                }
                onChange={(value) => {
                  const values = value as { value: string; label: string }[];
                  onChange(values.map((x) => x.value));
                }}
                options={
                  campaigns
                    ?.filter((c) => !menuCampaigns?.find((m) => m.id === c.id))
                    .map((c) => ({
                      value: c.id,
                      label: c.name,
                    })) || []
                }
                value={(value || []).map((x) => ({
                  value: x,
                  label: campaigns?.find((c) => c.id === x)?.name || "",
                }))}
                innerRef={ref}
                {...rest}
              />
            </FormRow>
          </div>
        </div>
        <div css={buttonWrapperStyle}>
          <ButtonInternalLink to={".."}>Cancel</ButtonInternalLink>
          <ButtonAction type="submit" disabled={methods.formState.isSubmitting}>
            {methods.formState.isSubmitting ? (
              <ActionSpinner />
            ) : (
              "Add campaigns"
            )}
          </ButtonAction>
        </div>
      </form>
    </FormProvider>
  );
};
